<script setup lang="ts">

interface Props {
  member: any
  index: number
  isVisible?: boolean
  isFlipped: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isVisible: true,
  isFlipped: false
})

const emit = defineEmits(['flip'])

const fullName = computed(() => `${props.member.firstName} ${props.member.lastName}`)

const toggleFlip = () => {
  emit('flip')
}
</script>

<template>
  <div 
    class="group perspective h-[520px] cursor-pointer transition-all duration-700"
    :class="isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'"
    :style="{ transitionDelay: (300 + (index * 150)) + 'ms' }"
    @click="toggleFlip"
  >
    <!-- Flip Inner Container -->
    <article 
      class="relative w-full h-full transition-all duration-700 preserve-3d will-change-transform shadow-2xl rounded-[2.5rem]"
      :class="{ 'rotate-y-180': isFlipped }"
    >
      
      <!-- FRONT FACE -->
      <div data-face="front" :inert="isFlipped" class="absolute inset-0 w-full h-full backface-hidden rounded-[2.5rem] overflow-hidden bg-oxo-card-bg border-2 border-oxo-border group-hover:border-oxo-red transition-all duration-500 shadow-xl group-hover:shadow-oxo-red/20 flex flex-col">
          <!-- Image Container with fixed aspect ratio -->
          <div class="aspect-square w-full overflow-hidden bg-linear-to-br from-oxo-card-bg to-background relative shrink-0">
              <NuxtImg loading="lazy"
                v-if="member.proImage && !member.proImage.includes('ui-avatars.com')"
                :src="member.proImage"
                :alt="member.proImageAlt || `${member.firstName} ${member.lastName}`"
                class="h-full w-full object-cover object-center group-hover:scale-110 transition-all duration-700 ease-out grayscale brightness-75 group-hover:grayscale-0 group-hover:brightness-100"
                sizes="100vw sm:50vw lg:33vw"
              />
              <!-- Premium Placeholder -->
              <div v-else class="w-full h-full flex flex-col items-center justify-center relative overflow-hidden">
                  <div class="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,var(--tw-gradient-stops))] from-oxo-red/40 via-transparent to-transparent"></div>
                  <div class="font-display font-black text-6xl text-white/10 group-hover:text-oxo-red/30 transition-all duration-700 uppercase select-none">
                      {{ member.firstName.charAt(0) }}{{ member.lastName.charAt(0) }}
                  </div>
              </div>
          </div>

          <!-- Fixed height text area -->
          <div class="grow p-6 flex flex-col relative bg-oxo-card-bg/95">
              <div class="h-[75px] flex flex-col justify-end mb-2">
                  <h3 class="text-foreground font-display font-black text-2xl lg:text-3xl dark:group-hover:text-oxo-yellow transition-colors duration-300 line-clamp-2 leading-tight">
                      {{ member.firstName }} <br/> {{ member.lastName }}
                  </h3>
              </div>
              
              <div class="h-[45px] flex items-start">
                  <p class="text-foreground-dim text-[10px] font-black uppercase tracking-[0.15em] leading-relaxed line-clamp-2">
                      {{ member.jobTitle }}
                  </p>
              </div>
              
              <!-- Premium "Flip" Button -->
              <div class="absolute bottom-6 right-6">
                  <button
                    type="button"
                    :aria-label="`Voir la biographie de ${fullName}`"
                    :aria-expanded="isFlipped"
                    class="w-10 h-10 rounded-2xl bg-white/10 backdrop-blur-md border border-oxo-border flex items-center justify-center text-foreground-dim group-hover:bg-oxo-red group-hover:text-white group-hover:scale-110 transition-all duration-500 shadow-xl">
                      <i class="fas fa-undo text-xs" aria-hidden="true"></i>
                  </button>
              </div>
          </div>
      </div>

      <!-- BACK FACE -->
      <div data-face="back" :inert="!isFlipped" class="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-oxo-card-bg rounded-[2.5rem] p-8 flex flex-col items-center justify-between text-center border border-oxo-border shadow-3xl overflow-hidden group">
          <div class="relative z-10 flex flex-col items-center w-full">
              <div class="w-20 h-20 rounded-2xl overflow-hidden border-2 border-oxo-red/50 shadow-lg shadow-oxo-red/10 mb-5 shrink-0 group-hover:rotate-6 transition-transform duration-500">
                  <NuxtImg loading="lazy"
                    v-if="member.proImage && !member.proImage.includes('ui-avatars.com')"
                    :src="member.proImage"
                    :alt="`${member.firstName} ${member.lastName}`"
                    class="w-full h-full object-cover"
                    sizes="80px"
                  />
                   <div v-else class="w-full h-full bg-background flex items-center justify-center font-display font-bold text-foreground-dim">
                      {{ member.firstName.charAt(0) }}{{ member.lastName.charAt(0) }}
                  </div>
              </div>
              
              <h3 class="text-foreground font-display font-black text-2xl mb-1 truncate w-full">{{ member.firstName }} {{ member.lastName }}</h3>
              <div class="h-0.5 w-8 bg-oxo-red/50 mb-4 group-hover:w-16 transition-all duration-500"></div>
              
              <div
                tabindex="0"
                :aria-label="`Biographie de ${fullName}`"
                class="prose prose-slate dark:prose-invert text-sm text-foreground-dim leading-relaxed max-h-[160px] overflow-y-auto mb-4 custom-scrollbar pr-2 font-light">
                  {{ member.bio || "Biographie en cours de rédaction par l'école OXO pour vous présenter ce membre de l'équipe." }}
              </div>
          </div>

          <div class="flex items-center justify-center gap-6 mt-2">
               <a v-if="member && member.linkedin" :href="member.linkedin" target="_blank" rel="noopener noreferrer" :aria-label="`LinkedIn de ${fullName}`" class="w-11 h-11 rounded-2xl bg-oxo-card-bg/20 border border-oxo-border flex items-center justify-center text-foreground-dim hover:text-white hover:bg-[#0077b5] hover:border-[#0077b5] transition-all duration-300 transform hover:scale-110" @click.stop>
                  <i class="fab fa-linkedin text-lg" aria-hidden="true"></i>
              </a>
              <button type="button" :aria-label="`Fermer la biographie de ${fullName}`" class="w-11 h-11 rounded-2xl bg-oxo-card-bg/20 border border-oxo-border flex items-center justify-center text-foreground-dim hover:text-foreground hover:bg-oxo-card-bg/40 transition-all duration-300" @click.stop="toggleFlip">
                  <i class="fas fa-times text-sm" aria-hidden="true"></i>
              </button>
          </div>
      </div>

    </article>
  </div>
</template>

<style scoped>
.perspective {
  perspective: 1000px;
}
.preserve-3d {
  transform-style: preserve-3d;
}
.backface-hidden {
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  -moz-backface-visibility: hidden;
  /* Firefox fix: force GPU layer */
  transform: translateZ(0);
}
.rotate-y-180 {
  transform: rotateY(180deg);
}

/* Firefox-specific fix for front face when flipped */
.rotate-y-180 .backface-hidden:first-child {
  visibility: hidden;
}
.rotate-y-180 .backface-hidden:last-child {
  visibility: visible;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #E53935;
  border-radius: 4px;
}
</style>
